import { useMemo, useState } from 'react';
import {
  InfiniteData,
  UseMutationResult,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { locationsKeys, trialLocationsKeys, trialsKeys } from '@data/constants';
import {
  ListTrialLocationView,
  ListUniqueLocationView,
  LocationApiAddEffectRequest,
  LocationApiAddLocationContactRequest,
  LocationApiGetLocationsRequest,
  LocationUpdateBody,
  LocationView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError } from '@data/utils/types';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useUpdateLocation = (
  trialId: string,
): UseMutationResult<
  unknown,
  APIError<LocationUpdateBody>,
  { id: string; values: LocationUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.location.updateLocation({
        id,
        locationUpdateBody: values,
      });
      return response.data;
    },
    onMutate: async ({ id, values }) => {
      await queryClient.cancelQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });

      const previousLocations = queryClient.getQueryData<InfiniteData<ListTrialLocationView>>(
        trialLocationsKeys.list({ id: trialId }),
      );

      if (previousLocations) {
        const newPages = previousLocations.pages.map((page: { items: LocationView[] }) => ({
          ...page,
          items: page.items.map((contact) =>
            contact.id === id ? { ...contact, ...values } : contact,
          ),
        }));

        queryClient.setQueryData(trialLocationsKeys.list({ id: trialId }), {
          ...previousLocations,
          pages: newPages,
        });
      }

      return { previousLocations };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });
    },

    onError: (_, __, context) =>
      queryClient.setQueryData(
        trialLocationsKeys.list({ id: trialId }),
        context?.previousLocations,
      ),
  });
};

export const useDeleteLocation = (
  trialId: string,
): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await MatchingApi.location.deleteLocation({ id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
    },
  });
};

export const useAddLocationEffect = (): UseMutationResult<
  void,
  APIError,
  LocationApiAddEffectRequest & { trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.location.addEffect(values);
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });
    },
  });
};

export const useDeleteLocationEffect = (): UseMutationResult<
  void,
  APIError,
  { id: string; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }) => {
      const response = await MatchingApi.location.removeEffect({ id });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });
    },
  });
};

export const useLocationsInfiniteList = () => {
  const [query, setQuery] = useState<LocationApiGetLocationsRequest>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery<
    ListUniqueLocationView,
    APIError
  >({
    queryKey: locationsKeys.list({ ...query, searchString: debouncedSearch }),
    initialPageParam: { ...query, searchString: debouncedSearch },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.location.getLocations(
        pageParam as LocationApiGetLocationsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const locations = data?.pages.flatMap((page) => page.items) || [];

  const locationsOptions = useMemo(
    () =>
      locations.map((location) => ({
        label: location.facility || location.city,
        value: location.facility || location.city,
      })),
    [locations],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    locations,
    locationsOptions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    setQuery,
    count: data?.pages[0].count || 0,
    isFirstLoading: isLoading,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useAddLocationContact = (): UseMutationResult<
  void,
  APIError,
  { body: LocationApiAddLocationContactRequest; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ body }) => {
      const response = await MatchingApi.location.addLocationContact(body);
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.list({ id: trialId }) });
    },
  });
};
